// Entrance animations - IntersectionObserver-driven .is-revealed toggle.
// Native browser APIs only, no external library. Honors prefers-reduced-motion.
import { qsa } from '../lib/dom.js';

const REDUCED = typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let sharedObserver = null;

function getObserver() {
  if (sharedObserver || typeof IntersectionObserver === 'undefined') return sharedObserver;
  sharedObserver = new IntersectionObserver((entries, obs) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.remove('ap-anim-pending');
      entry.target.classList.add('is-revealed');
      obs.unobserve(entry.target);
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
  return sharedObserver;
}

/**
 * Walks `[data-anim]` inside `root` and observes any that haven't been revealed yet.
 * Safe to call repeatedly (e.g., after catalog re-render): already-revealed elements
 * are skipped, and IntersectionObserver dedupes its own observe() calls.
 */
export function initRevealOnScroll(root = document) {
  if (REDUCED || typeof IntersectionObserver === 'undefined') {
    qsa('[data-anim]:not(.is-revealed)', root).forEach((el) => el.classList.add('is-revealed'));
    return;
  }
  const obs = getObserver();
  const vh = window.innerHeight || document.documentElement.clientHeight || 0;
  qsa('[data-anim]:not(.is-revealed)', root).forEach((el) => {
    // Hero titles drive their own per-character reveal (revealTitle) - never
    // hijack char spans here.
    if (el.dataset.anim === 'char') return;
    // Content is VISIBLE by default (see entrance-animations.css). Only
    // elements strictly BELOW the fold get hidden + scroll-animated - they're
    // off-screen when we hide them, so nothing visible ever flashes. Anything
    // already painted (in or above the viewport) reveals instantly with the
    // animation suppressed, so the deferred bundle landing late can't replay it.
    const r = el.getBoundingClientRect();
    if (r.top >= vh) {
      el.classList.add('ap-anim-pending');
      obs.observe(el);
    } else {
      el.classList.add('ap-anim-instant', 'is-revealed');
    }
  });
}

const splitTitles = new WeakSet();

// Build [<word-span>, …, " ", <word-span>, …] from a chunk of text.
// Each word-span is `display: inline-block; white-space: nowrap` so the browser
// only breaks the line at real word boundaries (whitespace becomes a text node).
function buildWordNodes(text, counter) {
  const out = [];
  const parts = text.split(/(\s+)/);
  parts.forEach((part) => {
    if (!part) return;
    if (/^\s+$/.test(part)) { out.push(document.createTextNode(part)); return; }
    const wordSpan = document.createElement('span');
    wordSpan.className = 'ap-reveal-word';
    [...part].forEach((char) => {
      const c = document.createElement('span');
      c.dataset.anim = 'char';
      c.style.setProperty('--char-i', counter.value);
      counter.value += 1;
      c.textContent = char;
      wordSpan.appendChild(c);
    });
    out.push(wordSpan);
  });
  return out;
}

// Pure-punctuation tokens (",", ".", ":", "-", etc.) shouldn't form their own
// non-breaking word - otherwise the browser can break a line between the prior
// word and the punctuation. After the split, glue each pure-punctuation word
// into the preceding non-punct word so they wrap as one unit.
function gluePunctuation(el) {
  const PUNCT = /^[\p{P}\p{S}]+$/u;
  const words = [...el.querySelectorAll('.ap-reveal-word')];
  for (let i = words.length - 1; i >= 0; i--) {
    const w = words[i];
    if (!PUNCT.test(w.textContent)) continue;
    let prev = null;
    for (let j = i - 1; j >= 0; j--) {
      if (!PUNCT.test(words[j].textContent)) { prev = words[j]; break; }
    }
    if (!prev) continue;
    while (w.firstChild) prev.appendChild(w.firstChild);
    w.remove();
  }
}

/**
 * Splits `el` into per-word, per-character spans then schedules the staggered
 * reveal on the next frame. Words are kept non-breaking via `.ap-reveal-word`
 * so the line never breaks mid-word. Existing element children (e.g. an inner
 * `<span class="ap-accent">`) are preserved so styling/colors stay intact.
 * Memoized per element so calling it repeatedly is cheap.
 */
export function revealTitle(el) {
  if (!el) return;
  if (!splitTitles.has(el)) {
    const counter = { value: 0 };
    const childNodes = [...el.childNodes];
    el.textContent = '';
    childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        buildWordNodes(node.textContent || '', counter).forEach((n) => el.appendChild(n));
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const wrapper = node.cloneNode(false); // shallow clone keeps tag + classes
        [...node.childNodes].forEach((inner) => {
          if (inner.nodeType === Node.TEXT_NODE) {
            buildWordNodes(inner.textContent || '', counter).forEach((n) => wrapper.appendChild(n));
          } else {
            wrapper.appendChild(inner.cloneNode(true));
          }
        });
        el.appendChild(wrapper);
      } else {
        el.appendChild(node.cloneNode(true));
      }
    });
    gluePunctuation(el);
    splitTitles.add(el);
  }
  // Reset (in case slide is re-shown), then trigger the animation on next frame.
  const chars = el.querySelectorAll('[data-anim="char"]');
  chars.forEach((s) => s.classList.remove('is-revealed'));
  // Force reflow so the animation restart actually runs
  void el.offsetWidth;
  if (REDUCED) {
    chars.forEach((s) => s.classList.add('is-revealed'));
    return;
  }
  requestAnimationFrame(() => {
    chars.forEach((s) => s.classList.add('is-revealed'));
  });
}
